import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import cls from 'classnames';
import { showEmailLogin, setLoginV2SpinnerOrigin } from '../../actions/LoginActions';
import { loginSpinnerOrigin } from '../../constants/Enums';
import { renderReactComponentWithRedux } from '../../hocs/withReduxProvider';
import { virtualBannerSelector } from '../../selectors/RingSelectors';
import { sendCustomClick, trackSystemEntityEvent } from '../../utils/SnowplowUtils';
import { systemInfo, buttonTypes, bannerSubcategory } from '../../constants/SnowplowConst';

// the biggest possible timeout delay is 24 days
// to don't overload system and make it more light weight
const delayDays = 24;
const [BEFORE, AFTER, NOW] = ['BEFORE', 'AFTER', 'NOW'];
const definedText = {
    [BEFORE]: [
        'Start Preparing for the Event!',
        'Pre-select your favorite sessions, sync them with your calendar and more.',
        "Let's Go",
    ],
    [NOW]: [
        'Join the Event!',
        'Be part of the entire event experience including sessions, networking and more.',
        "Let's Go",
    ],
    [AFTER]: [
        'Look Back at the Event',
        'Find out which sessions took place and who spoke and sponsored.',
        "Let's Go",
    ]
};
const customTexts = {};

const bannerClassMapper = {
    [NOW]: 'is-live-now',
    [BEFORE]: 'is-upcoming',
    [AFTER]: 'is-ended',
};

const checkForCustomText = (accountId, eventId) => {
    const hasCustomText = customTexts?.[accountId]?.events.includes(eventId);

    return hasCustomText ? customTexts[accountId].text : null;
};

const VirtualVenueBanner = ({
    isRingWorks,
    eventTimeZone,
    startDate,
    endDate,
    customWrapperClass,
    showEmailLogin,
    setLoginV2SpinnerOrigin,
    loginV2LoadingState,
    eventGroupId,
    authDropRedirectRing,
    userId,
    eventId,
    accountId,
    isShowCustomBannerData,
    customBannerHeader,
    customBannerText,
    customBannerCTA
}) => {
    const [status, changeCurrentStatus] = useState(BEFORE);
    useEffect(() => {
        if (!isRingWorks) return;
        checkTimeStatus();
        trackSystemEntityEvent(systemInfo.ELEMENT_LOADED, 'onboarding banner loaded');
        return () => cleanTimersIfExists();
    }, [isRingWorks]);

    if (!isRingWorks) return null;
    let startTimer = null;
    let finishTimer = null;
    const eventStartsDate = moment.tz(startDate, eventTimeZone).valueOf();
    const eventFinishesDate = moment.tz(endDate, eventTimeZone).valueOf();
    const possibleDelayEventStart = moment(eventStartsDate).subtract(delayDays, 'day').valueOf();
    const possibleDelayEventFinish = moment(eventFinishesDate).subtract(delayDays, 'day').valueOf();
    const currentTimeDate = moment.tz(eventTimeZone).valueOf();
    const checkTimeStatus = () => {
        if (currentTimeDate >= eventStartsDate && currentTimeDate <= eventFinishesDate) {
            changeCurrentStatus(NOW);
            startTimersIfNeeds(NOW);
        } else if (currentTimeDate < eventStartsDate && currentTimeDate < eventFinishesDate) {
            changeCurrentStatus(BEFORE);
            startTimersIfNeeds(BEFORE);
        } else if (currentTimeDate > eventStartsDate && currentTimeDate > eventFinishesDate) {
            changeCurrentStatus(AFTER);
        }
    };

    const startTimersIfNeeds = (status) => {
        switch (status) {
        case NOW: {
            cleanTimersIfExists();
            if (currentTimeDate >= possibleDelayEventFinish) {
                const delay = moment(eventFinishesDate).diff(moment(currentTimeDate));
                startCurrentTimers(finishTimer, AFTER, delay);
            }
            break;
        }
        case BEFORE: {
            cleanTimersIfExists();
            if (currentTimeDate >= possibleDelayEventFinish) {
                const delay = moment(eventFinishesDate).diff(moment(currentTimeDate));
                startCurrentTimers(finishTimer, AFTER, delay);
            }
            if (currentTimeDate >= possibleDelayEventStart) {
                const delay = moment(eventStartsDate).diff(moment(currentTimeDate));
                startCurrentTimers(startTimer, NOW, delay);
            }
            break;
        }
        }
    };

    const startCurrentTimers = (ref, status, delay) => {
        ref = setTimeout(() => {
            ref = null;
            changeCurrentStatus(status);
        }, delay);
    };

    const cleanTimersIfExists = () => {
        if (startTimer) {
            clearTimeout(startTimer);
            startTimer = null;
        }
        if (finishTimer) {
            clearTimeout(finishTimer);
            finishTimer = null;
        }
    };

    const handleLogin = (e) => {
        sendCustomClick(buttonTypes.onBoardingBanner, bannerSubcategory, e.target);

        if (userId) {
            const query = new URLSearchParams({
                eventId,
                groupId: eventGroupId,
                redirectUrl: authDropRedirectRing,
            });
            const url = `/auth/ring/extractAndTransferTokens?${query}`;
            window.location.replace(url);
        }

        showEmailLogin();
        setLoginV2SpinnerOrigin?.(loginSpinnerOrigin.VIRTUAL_VENUE_BANNER);
    };

    const customText = checkForCustomText(accountId, eventId);
    const text = customText || definedText;
    const [headerText, innerText, buttonText] = text[status];

    const wrapperClass = cls('main_ring-banner-wrapper ring-button-background', {
        [customWrapperClass]: customWrapperClass,
        'the-venue-custom-banner': !!customText,
    });

    const statusClass = cls('virtual-venue-banner-status', bannerClassMapper[status]);

    const descriptionClass = cls('virtual-venue-banner-description', bannerClassMapper[status]);
    const ctaClass = cls('virtual-venue-banner-cta', 'ring-button-hover-color', bannerClassMapper[status]);

    const isLoginV2Loading = loginV2LoadingState.isLoading
        && loginV2LoadingState.spinnerOrigin === loginSpinnerOrigin.VIRTUAL_VENUE_BANNER;

    return (
        isRingWorks
            ? <div className={wrapperClass} data-test="virtual-venue-banner">
                <div className="virtual-venue-banner-inner">
                    <div className={statusClass}>
                        <h2>{isShowCustomBannerData ? customBannerHeader : headerText}</h2>
                        <svg
                            className="virtual-venue-banner-animated-svg"
                            width="540"
                            height="506"
                            viewBox="0 0 540 506"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M118.327 113.123C150.508 83.4871 185.789 52.7518 230.244 44.0792C274.699 35.4065 328.266 49.0786 375.753 72.6517C423.178 96.5068 464.865 130.337 478.038 168.618C491.553 206.973 476.957 249.57 475.151 300.843C473.406 351.834 484.451 411.5 456.846 444.784C429.522 478.425 363.891 485.757 307.382 474.104C250.934 462.17 203.608 431.251 161.163 402.571C118.377 373.816 80.4112 347.582 59.0591 314.024C37.7681 280.184 32.6879 239.229 44.7671 204.943C56.9075 170.375 86.2074 142.477 118.327 113.123Z" fill="white" />
                        </svg>
                    </div>
                    <div className={descriptionClass}>
                        <h2>{isShowCustomBannerData ? customBannerHeader : headerText}</h2>
                        <span>{isShowCustomBannerData ? customBannerText : innerText}</span>
                    </div>
                    <div>
                        <button
                            className={ctaClass}
                            type="button"
                            onClick={handleLogin}
                            data-test="virtual-venue-banner-cta"
                        >
                            {isLoginV2Loading
                                ? <div className="virtual-venue-banner-spinner ring-button-color" />
                                : isShowCustomBannerData ? customBannerCTA : buttonText}
                        </button>
                    </div>
                </div>
            </div>
            : null
    );
};

const VirtualVenueBannerWithRedux = connect(
    virtualBannerSelector,
    { showEmailLogin, setLoginV2SpinnerOrigin }
)(VirtualVenueBanner);

export const renderVirtualVenueBanner = (props = null) => {
    renderReactComponentWithRedux(
        VirtualVenueBannerWithRedux,
        document.getElementById('virtual-venue-banner'),
        props
    );
};

export default VirtualVenueBannerWithRedux;
